/**
 * SectionTreeRenderer — render recursivo del árbol de secciones del
 * template materializando el `content` del artifact.
 *
 * Tres tipos de nodo:
 *
 *   1. **Hoja** (rich_text, plain_text, lists, tables, mermaid, etc.)
 *      → renderiza `<SectionEditor>`.
 *
 *   2. **Container con repeatable child** (ej. `casos_de_uso` con
 *      `caso_de_uso` repeatable adentro) → renderiza
 *      `<RepeatableGroupEditor>` que itera sobre el array de instancias
 *      en `content[container.section_id]`. Cada instancia es una
 *      tarjeta colapsable con sus children renderizados recursivamente.
 *
 *   3. **Container transparente** (`fixed_heading` sin repeatable child)
 *      → renderiza un header sutil + recursa sobre children al mismo
 *      nivel del path. Convención HIVA: containers transparentes NO
 *      crean anidamiento en el content.
 *
 * Sprint AI-1.f — añade `currentPath` que viaja con cada hoja hasta el
 * modal de IA y de ahí al backend, permitiendo al resolver activar los
 * casos 2/3 (instancia activa) en lugar del caso 4 (array completo).
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react'

import { SectionEditor } from './SectionEditor'
import { type AvailableTokenSection } from '../../ai/SectionDraftDialog'
import {
  appendInstance,
  type PathSegment,
  removeInstance,
  type TemplateI18nSection,
  type TemplateSectionMeta,
} from './section_types'

interface AiContext {
  artifactId: string
  /** Versión draft activa — propagada a SectionEditor para invalidar
   * el cache de citas IA después de aceptar un draft (ADR-0001 Tier 1).
   * Si no se pasa, la persistencia funciona pero el viewer no se
   * refresca hasta el próximo fetch. */
  versionId?: string
  projectLanguage: string
  availableTokenSections?: AvailableTokenSection[]
  /** Sprint AI-1.f.bug2: callback de auto-save que se ejecuta antes
   * de abrir el modal de IA en cualquier hoja. */
  onBeforeAiOpen?: () => Promise<void>
}

export interface SectionTreeRendererProps {
  node: TemplateSectionMeta
  /** Path al PADRE del nodo. El renderer le agrega su propio segmento
   * según corresponda (hoja: index undefined; instancia repetible:
   * index numérico). */
  parentPath: PathSegment[]
  /** Content COMPLETO del artifact (root-level dict). Se navega con
   * `currentPath + node.section_id` para extraer el value de este
   * nodo. */
  content: Record<string, unknown>
  /** Callback que recibe el NUEVO content completo. El renderer usa
   * `setValueAtPath` para componer el patch sin mutar. */
  onContentChange: (next: Record<string, unknown>) => void
  /** Mapa section_id → i18n section, pre-computado por el padre para
   * evitar O(n) en cada nodo. */
  i18nMap: Map<string, TemplateI18nSection>
  disabled?: boolean
  ai?: AiContext
}

export function SectionTreeRenderer({
  node,
  parentPath,
  content,
  onContentChange,
  i18nMap,
  disabled = false,
  ai,
}: SectionTreeRendererProps) {
  const i18n = i18nMap.get(node.section_id)
  const title = i18n?.title ?? node.section_id

  const children = (node['children'] as TemplateSectionMeta[] | undefined) ?? []
  const isContainer = children.length > 0
  const repeatableChild = children.find(
    (c) => (c as { repeatable?: boolean }).repeatable === true,
  )

  // Tipo (a) Container con repeatable child → RepeatableGroupEditor
  if (isContainer && repeatableChild) {
    return (
      <RepeatableGroupEditor
        container={node}
        repeatable={repeatableChild}
        parentPath={parentPath}
        content={content}
        onContentChange={onContentChange}
        i18nMap={i18nMap}
        disabled={disabled}
        {...(ai ? { ai } : {})}
      />
    )
  }

  // Tipo (b) Container transparente → header + children recursivos al
  // mismo nivel de path. Convención: el container NO entra al path.
  if (isContainer) {
    return (
      <section className="space-y-3">
        <h2 className="font-display text-base font-semibold text-ink-muted border-b border-line pb-1">
          {title}
        </h2>
        <div className="space-y-3 pl-3 border-l-2 border-line/60">
          {[...children]
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            .map((child) => (
              <SectionTreeRenderer
                key={child.section_id}
                node={child}
                parentPath={parentPath}
                content={content}
                onContentChange={onContentChange}
                i18nMap={i18nMap}
                disabled={disabled}
                {...(ai ? { ai } : {})}
              />
            ))}
        </div>
      </section>
    )
  }

  // Tipo (c) Hoja → SectionEditor con currentPath = [...parentPath,
  // {section_id: node.section_id}]. La hoja NUNCA tiene index — el
  // index lo lleva el segmento del container repetible.
  const leafPath: PathSegment[] = [
    ...parentPath,
    { section_id: node.section_id },
  ]
  // Para extraer el value de la hoja, navegamos hasta su padre y luego
  // tomamos `[node.section_id]`. Pero `getValueAtPath` con `leafPath`
  // ya hace exactamente eso (último segmento sin index = key normal).
  const leafValue = readValueAtLeafPath(content, leafPath)

  const handleLeafChange = (next: unknown) => {
    onContentChange(writeValueAtLeafPath(content, leafPath, next))
  }

  return (
    <SectionEditor
      meta={node}
      title={title}
      value={leafValue}
      onChange={handleLeafChange}
      disabled={disabled}
      {...(i18n?.placeholder ? { placeholder: i18n.placeholder } : {})}
      {...(i18n?.help_text ? { helpText: i18n.help_text } : {})}
      {...(ai
        ? {
            ai: {
              artifactId: ai.artifactId,
              ...(ai.versionId ? { versionId: ai.versionId } : {}),
              projectLanguage: ai.projectLanguage,
              ...(ai.availableTokenSections
                ? { availableTokenSections: ai.availableTokenSections }
                : {}),
              ...(ai.onBeforeAiOpen
                ? { onBeforeAiOpen: ai.onBeforeAiOpen }
                : {}),
              currentPath: leafPath,
            },
          }
        : {})}
    />
  )
}

// ─── RepeatableGroupEditor ──────────────────────────────────────────────────
function RepeatableGroupEditor({
  container,
  repeatable,
  parentPath,
  content,
  onContentChange,
  i18nMap,
  disabled,
  ai,
}: {
  container: TemplateSectionMeta
  repeatable: TemplateSectionMeta
  parentPath: PathSegment[]
  content: Record<string, unknown>
  onContentChange: (next: Record<string, unknown>) => void
  i18nMap: Map<string, TemplateI18nSection>
  disabled: boolean
  ai?: AiContext
}) {
  const { t } = useTranslation('projects')
  const containerI18n = i18nMap.get(container.section_id)
  const repeatableI18n = i18nMap.get(repeatable.section_id)
  const containerTitle = containerI18n?.title ?? container.section_id
  const repeatableTitle = repeatableI18n?.title ?? repeatable.section_id

  // El array vive bajo el SECTION_ID DEL CONTAINER (convención HIVA).
  const containerPath: PathSegment[] = [
    ...parentPath,
    { section_id: container.section_id },
  ]
  const arrAtPath = readArrayAt(content, containerPath)
  const instances = arrAtPath ?? []

  const minItems =
    typeof container['min_items'] === 'number'
      ? (container['min_items'] as number)
      : typeof repeatable['min_items'] === 'number'
        ? (repeatable['min_items'] as number)
        : undefined
  const maxItems =
    typeof container['max_items'] === 'number'
      ? (container['max_items'] as number)
      : typeof repeatable['max_items'] === 'number'
        ? (repeatable['max_items'] as number)
        : undefined

  const canAdd =
    !disabled && (maxItems === undefined || instances.length < maxItems)
  const canRemove = (i: number): boolean =>
    !disabled &&
    (minItems === undefined || instances.length > minItems) &&
    i >= 0 &&
    i < instances.length

  const handleAdd = () => {
    onContentChange(appendInstance(content, containerPath, {}))
  }

  const handleRemove = (index: number) => {
    onContentChange(removeInstance(content, containerPath, index))
  }

  // Children del repeatable que se renderizan dentro de cada instancia.
  // El path para CADA instancia es `[...parentPath, { container, index: i }]`.
  // El section_id del repeatable singular NUNCA entra al path.
  const repChildren =
    (repeatable['children'] as TemplateSectionMeta[] | undefined) ?? []

  return (
    <section className="space-y-3 rounded-md border border-line bg-surface p-4">
      <header className="flex items-center justify-between gap-2">
        <div>
          <h2 className="font-display text-base font-semibold text-ink">
            {containerTitle}
          </h2>
          <p className="text-[11px] text-ink-subtle font-body">
            {t('official_editor.repeatable_count', {
              count: instances.length,
              singular: repeatableTitle,
              defaultValue: '{{count}} {{singular}}',
            })}
            {minItems !== undefined && ` · min ${minItems}`}
            {maxItems !== undefined && ` · max ${maxItems}`}
          </p>
        </div>
        <button
          type="button"
          onClick={handleAdd}
          disabled={!canAdd}
          className="inline-flex items-center gap-1.5 rounded-md border border-brand/40 bg-brand/5 px-2 py-1 text-xs font-medium text-brand hover:bg-brand/10 disabled:opacity-50"
        >
          <Plus className="w-3 h-3" />
          {t('official_editor.repeatable_add', {
            singular: repeatableTitle,
            defaultValue: 'Agregar {{singular}}',
          })}
        </button>
      </header>

      {instances.length === 0 ? (
        <p className="text-xs italic text-ink-subtle py-3 text-center">
          {t('official_editor.repeatable_empty', {
            singular: repeatableTitle,
            defaultValue: 'Sin instancias. Agrega la primera.',
          })}
        </p>
      ) : (
        <ol className="space-y-2">
          {instances.map((_inst, i) => (
            <RepeatableInstanceCard
              key={`${container.section_id}-${i}`}
              container={container}
              repeatable={repeatable}
              repeatableTitle={repeatableTitle}
              index={i}
              parentPath={parentPath}
              content={content}
              onContentChange={onContentChange}
              i18nMap={i18nMap}
              disabled={disabled}
              repChildren={repChildren}
              canRemove={canRemove(i)}
              onRemove={() => handleRemove(i)}
              {...(ai ? { ai } : {})}
            />
          ))}
        </ol>
      )}
    </section>
  )
}

function RepeatableInstanceCard({
  container,
  repeatable: _repeatable, // eslint-disable-line @typescript-eslint/no-unused-vars
  repeatableTitle,
  index,
  parentPath,
  content,
  onContentChange,
  i18nMap,
  disabled,
  repChildren,
  canRemove,
  onRemove,
  ai,
}: {
  container: TemplateSectionMeta
  repeatable: TemplateSectionMeta
  repeatableTitle: string
  index: number
  parentPath: PathSegment[]
  content: Record<string, unknown>
  onContentChange: (next: Record<string, unknown>) => void
  i18nMap: Map<string, TemplateI18nSection>
  disabled: boolean
  repChildren: TemplateSectionMeta[]
  canRemove: boolean
  onRemove: () => void
  ai?: AiContext
}) {
  const { t } = useTranslation('projects')
  const [collapsed, setCollapsed] = useState(false)

  // Path al INTERIOR de esta instancia: [...parentPath, {container, index}]
  // Los children del repeatable singular se renderizan con este path
  // como su parentPath. NUNCA agregamos el section_id del repeatable.
  const instancePath: PathSegment[] = [
    ...parentPath,
    { section_id: container.section_id, index },
  ]

  return (
    <li className="rounded-md border border-line bg-canvas">
      <header className="flex items-center justify-between gap-2 px-3 py-2 border-b border-line">
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          className="flex items-center gap-1.5 text-sm text-ink hover:text-brand"
        >
          {collapsed ? (
            <ChevronRight className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
          <span className="font-medium">
            {repeatableTitle} #{index + 1}
          </span>
        </button>
        <button
          type="button"
          onClick={onRemove}
          disabled={!canRemove}
          aria-label={t('official_editor.repeatable_remove', {
            singular: repeatableTitle,
            defaultValue: 'Eliminar {{singular}}',
          })}
          className="text-ink-subtle hover:text-critical disabled:opacity-30"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </header>
      {!collapsed && (
        <div className="space-y-3 p-3">
          {[...repChildren]
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            .map((child) => (
              <SectionTreeRenderer
                key={child.section_id}
                node={child}
                parentPath={instancePath}
                content={content}
                onContentChange={onContentChange}
                i18nMap={i18nMap}
                disabled={disabled}
                {...(ai ? { ai } : {})}
              />
            ))}
        </div>
      )}
    </li>
  )
}

// ─── Helpers locales (path-aware reads/writes) ──────────────────────────────
/** Lectura del value en una hoja. Encapsula la lógica de path para no
 * exponer en el componente. */
function readValueAtLeafPath(
  content: Record<string, unknown>,
  leafPath: PathSegment[],
): unknown {
  let cursor: unknown = content
  for (const seg of leafPath) {
    if (cursor === null || cursor === undefined) return undefined
    if (typeof cursor !== 'object') return undefined
    const next = (cursor as Record<string, unknown>)[seg.section_id]
    if (seg.index !== undefined) {
      if (!Array.isArray(next)) return undefined
      cursor = next[seg.index]
    } else {
      cursor = next
    }
  }
  return cursor
}

/** Escritura — reusa setValueAtPath del módulo de tipos. Wrapper local
 * solo para mantener la simetría con `readValueAtLeafPath`. */
function writeValueAtLeafPath(
  content: Record<string, unknown>,
  leafPath: PathSegment[],
  value: unknown,
): Record<string, unknown> {
  // Imported lazily through the types module to avoid cyclic imports.
  // Re-implementación inline (idéntica a section_types.setValueAtPath)
  // para no obligar a renombrar el wrapper. Usa la misma lógica.
  if (leafPath.length === 0) return (value ?? {}) as Record<string, unknown>
  const root: Record<string, unknown> = { ...content }
  let cursor: Record<string, unknown> = root
  for (let i = 0; i < leafPath.length - 1; i++) {
    const seg = leafPath[i]!
    if (seg.index !== undefined) {
      const arr = Array.isArray(cursor[seg.section_id])
        ? [...(cursor[seg.section_id] as unknown[])]
        : []
      while (arr.length <= seg.index) arr.push(undefined)
      const inst =
        arr[seg.index] && typeof arr[seg.index] === 'object'
          ? { ...(arr[seg.index] as Record<string, unknown>) }
          : {}
      arr[seg.index] = inst
      cursor[seg.section_id] = arr
      cursor = inst
    } else {
      const sub =
        cursor[seg.section_id] && typeof cursor[seg.section_id] === 'object'
          ? { ...(cursor[seg.section_id] as Record<string, unknown>) }
          : {}
      cursor[seg.section_id] = sub
      cursor = sub as Record<string, unknown>
    }
  }
  const last = leafPath[leafPath.length - 1]!
  if (last.index !== undefined) {
    const arr = Array.isArray(cursor[last.section_id])
      ? [...(cursor[last.section_id] as unknown[])]
      : []
    while (arr.length <= last.index) arr.push(undefined)
    arr[last.index] = value
    cursor[last.section_id] = arr
  } else {
    cursor[last.section_id] = value
  }
  return root
}

/** Lee el value en `path` esperando que sea un array (para repetibles).
 * Devuelve undefined si no es array. */
function readArrayAt(
  content: Record<string, unknown>,
  path: PathSegment[],
): unknown[] | undefined {
  const v = readValueAtLeafPath(content, path)
  return Array.isArray(v) ? v : undefined
}
